<script setup lang="ts">
import { HModal } from '@headless/atoms'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
}>(), {
  size: 'md',
  closeOnBackdrop: true,
  closeOnEscape: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()
</script>

<template>
  <HModal
    :modelValue="modelValue"
    :closeOnBackdrop="closeOnBackdrop"
    :closeOnEscape="closeOnEscape"
    @update:modelValue="emit('update:modelValue', $event)"
    v-slot="{ isOpen, close, onBackdropClick }"
  >
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="isOpen" class="modal-backdrop" @click="onBackdropClick">
          <div :class="['modal', `modal--${size}`]" @click.stop>
            <div v-if="title" class="modal__header">
              <h3 class="modal__title">{{ title }}</h3>
              <button type="button" class="modal__close" @click="close">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
            <div class="modal__body">
              <slot :close="close" />
            </div>
            <div v-if="$slots.footer" class="modal__footer">
              <slot name="footer" :close="close" />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </HModal>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-modal-backdrop);
  padding: var(--spacing-lg);
}

.modal {
  background-color: var(--colors-background-elevated);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  max-height: calc(100vh - var(--spacing-xl) * 2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: var(--z-index-modal);
}

.modal--sm { width: 100%; max-width: 400px; }
.modal--md { width: 100%; max-width: 560px; }
.modal--lg { width: 100%; max-width: 720px; }
.modal--xl { width: 100%; max-width: 960px; }

.modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--colors-border-default);
}

.modal__title {
  font-size: var(--typography-heading-h3-size);
  font-weight: var(--typography-heading-h3-weight);
  color: var(--colors-text-primary);
}

.modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--colors-text-secondary);
  transition: all var(--transition-fast);
}

.modal__close:hover {
  background-color: var(--colors-background-surface);
  color: var(--colors-text-primary);
}

.modal__close svg {
  width: 20px;
  height: 20px;
}

.modal__body {
  padding: var(--spacing-lg);
  overflow-y: auto;
}

.modal__footer {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--colors-border-default);
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--transition-normal);
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform var(--transition-normal);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: scale(0.95) translateY(-20px);
}
</style>
